import React, { useMemo } from 'react';
import { When } from 'react-if';

import { Box } from '@nerdwallet/currency/components/Box';
import { Button } from '@nerdwallet/nerdwallet-plus-components';
import { Button as CButton } from '@nerdwallet/currency/components/Button';
import { Type } from '@nerdwallet/currency/components/Type';
import NwReactCollapse from '@components/nw-react-collapse';

import Touchable from '@components/touchable';

import RewardStepStatusIcon from './reward-step-status-icon';

import styles from './reward-step-tile.module.scss';
import {
  FinancialNwPlusRewardStep,
  NwPlusRewardStepStatus,
  NwPlusRichText,
} from '@generated-gql/generated-types';

const buildRichText = (richText?: NwPlusRichText) => {
  if (!richText) {
    return;
  }

  const { text, links } = richText;

  if (links?.length === 0) {
    return text;
  }

  let finalText = text;

  links?.forEach((link) => {
    const { tag, url, text } = link ?? {};

    if (tag && url && text) {
      finalText = finalText.replace(
        tag,
        `<a href="${url}" target="_blank">${text}</a>`,
      );
    }
  });

  return <span dangerouslySetInnerHTML={{ __html: finalText }} />;
};

interface Props {
  step: FinancialNwPlusRewardStep;
  taglineSupplement?: JSX.Element;
  isExpanded?: boolean;
  showBottomBorder?: boolean;
  isLoading?: boolean;
  onSelectStep: () => void;
  onCtaClick?: () => void;
}

const RichRewardStepTile: React.FC<Props> = ({
  step,
  isExpanded = false,
  isLoading = false,
  onSelectStep,
  onCtaClick,
}) => {
  const {
    title,
    tagline,
    status,
    ctaText,
    alertMessage,
    infoMessage,
    failedTagLine,
  } = useMemo(() => {
    return {
      title: buildRichText(step.title),
      tagline: buildRichText(step.tagline),
      failedTagLine: buildRichText(step.failedTagline),
      status: step.status,
      ctaText: step.ctaText,
      alertMessage: buildRichText(step.alertMessage),
      infoMessage: buildRichText(step.infoMessage),
    };
  }, [step]);

  const isCompleted = status === NwPlusRewardStepStatus.Completed;
  const isFailed = status === NwPlusRewardStepStatus.Failed;
  const isPending = status === NwPlusRewardStepStatus.Pending;

  const isTextLighter = isCompleted || isPending;

  const notice = (() => {
    if (isFailed && failedTagLine) {
      return failedTagLine;
    }
    if (isPending && alertMessage) {
      return alertMessage;
    }
  })();

  return (
    <Box
      display="flex"
      paddingTop={2}
      paddingBottom={2}
      paddingX={{ mobile: '3', desktop: '4' }}
      borderColor="neutral-lighter"
      gridGap={2}
      flexDirection="row"
      alignItems="center"
      justifyContent="flex-start"
      width="100"
      className={styles.container}
    >
      <div className={styles.statusIcon}>
        <RewardStepStatusIcon status={status} />
      </div>
      <div className={styles.contentContainer}>
        <Touchable className={styles.titleContainer} onClick={onSelectStep}>
          <Type bold size="2" component="span" light={isTextLighter}>
            {title}
          </Type>
        </Touchable>
        <NwReactCollapse isOpened={isExpanded}>
          <Type size="1" component="span" light={isTextLighter}>
            {tagline}
          </Type>

          <When condition={!!notice}>
            <Box
              backgroundColor={
                status === NwPlusRewardStepStatus.Failed
                  ? 'red-lightest'
                  : 'yellow-lightest'
              }
              display="flex"
              flexDirection="row"
              alignItems="flex-start"
              gridGap={1}
              padding={2}
              marginTop={2}
              borderRadius={2}
            >
              <RewardStepStatusIcon status={status} />
              <Type size="1" component="span">
                {notice}
              </Type>
            </Box>
          </When>
          <When condition={!isCompleted && !isFailed && !!ctaText}>
            <Box marginTop={3}>
              {isLoading ? (
                <CButton primary loading onClick={onCtaClick}>
                  {ctaText?.text}
                </CButton>
              ) : (
                <Button onClick={onCtaClick}>{ctaText?.text}</Button>
              )}
            </Box>
          </When>
          <When
            condition={
              !alertMessage &&
              !!infoMessage &&
              status === NwPlusRewardStepStatus.NoStatus
            }
          >
            <Box
              backgroundColor="neutral-lightest"
              display="flex"
              flexDirection="row"
              alignItems="flex-start"
              gridGap={1}
              paddingX={3}
              paddingY={2}
              marginTop={3}
              marginRight={{ desktop: '3-half', mobile: 0 }}
              borderRadius={2}
            >
              <Type size="1" component="span">
                {infoMessage}
              </Type>
            </Box>
          </When>
        </NwReactCollapse>
      </div>
    </Box>
  );
};

export default RichRewardStepTile;
